import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  api,
  APIError,
  notificationsApi,
  sessionStore,
  type NotificationPreferences,
  type User,
} from '@/lib/api';
import { registerForPushAsync } from '@/lib/push';
import { AppTopbar, Btn, SignOutConfirm, useSignOutConfirm } from '@/components';
import { PaywallSheet } from '@/components/PaywallSheet';
import { friendlyError } from '@/lib/errors';

type ToggleKey =
  | 'rating_received'
  | 'rating_with_note'
  | 'ai_review_ready'
  | 'dm_message'
  | 'media_processed'
  | 'media_rejected'
  | 'payment_received'
  | 'payment_failed'
  | 'subscription_cancelled'
  | 'subscription_expired';

const TOGGLES: { key: ToggleKey; title: string; sub: string }[] = [
  { key: 'rating_received', title: 'Ratings', sub: "When someone says yes or no on your profile." },
  { key: 'rating_with_note', title: 'Notes', sub: 'When a rater leaves a written note.' },
  { key: 'ai_review_ready', title: 'AI reviews', sub: "When your AI review finishes generating." },
  { key: 'dm_message', title: 'Messages', sub: 'New DMs from users who rated you.' },
  { key: 'media_processed', title: 'Photo approved', sub: "When a photo upload finishes scanning + goes live." },
  { key: 'media_rejected', title: 'Photo rejected', sub: "When a photo can't be approved." },
  { key: 'payment_received', title: 'Subscription activated', sub: 'When your premium subscription starts or renews.' },
  { key: 'payment_failed', title: 'Payment issues', sub: "When we can't take payment for your subscription." },
  { key: 'subscription_cancelled', title: 'Cancellation', sub: 'When you cancel — confirms when premium ends.' },
  { key: 'subscription_expired', title: 'Subscription ended', sub: 'When premium expires.' },
];

export function SettingsPage() {
  const [prefs, setPrefs] = useState<NotificationPreferences | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Three states for browser-push permission: 'default' (never asked),
  // 'granted', 'denied'. We surface a CTA only when 'default' so users
  // can opt in deliberately rather than getting an unexplained system prompt.
  const [pushPermission, setPushPermission] = useState<NotificationPermission | 'unsupported'>(
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported',
  );
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // /auth/me cached for the Subscription section's plan badge.
  // No fallback — section just renders a loading skeleton until
  // the fetch lands.
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    // Auth gate lives in AppShell.
    (async () => {
      try {
        const p = await notificationsApi.getPrefs();
        setPrefs(p);
      } catch (e) {
        setError(friendlyError(e, 'Settings'));
      }
    })();
    // /auth/me powers the Subscription plan badge. Best-effort —
    // a failure here just leaves the badge in its loading state;
    // the rest of the page (notification prefs etc.) is unaffected.
    api.me().then(setUser).catch(() => null);
  }, []);

  const scheduleSave = useCallback((next: NotificationPreferences) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        const saved = await notificationsApi.updatePrefs(next);
        setPrefs(saved);
      } catch (e) {
        setError(friendlyError(e, 'Settings'));
      } finally {
        setSaving(false);
      }
    }, 250);
  }, []);

  const toggle = (key: ToggleKey) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!prefs) return;
    const next = { ...prefs, [key]: e.target.checked };
    setPrefs(next);
    scheduleSave(next);
  };

  const onEnablePush = async () => {
    const res = await registerForPushAsync();
    setPushPermission(Notification.permission);
    if (!res) {
      setError(
        Notification.permission === 'denied'
          ? 'Browser blocked notifications. Enable in your browser settings.'
          : "Couldn't enable push notifications.",
      );
    }
  };

  return (
    <>
      <AppTopbar
        eyebrow="Account"
        title="Settings"
      />
      <main style={{ maxWidth: 720, margin: '0', padding: '0 40px 60px' }}>

      {/* Browser push CTA — only when we haven't asked yet. */}
      {pushPermission === 'default' && (
        <div style={ctaBox}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              Get notified in this browser
            </div>
            <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
              We'll ping you when someone rates your profile or your AI review is ready.
            </div>
          </div>
          <button onClick={onEnablePush} style={ctaBtn}>Turn on</button>
        </div>
      )}
      {pushPermission === 'denied' && (
        <div style={mutedNotice}>
          Browser notifications are blocked. Enable in your browser settings to receive them here.
        </div>
      )}

      <Section title="Subscription" status="">
        <PlanBadge user={user} />
      </Section>

      <SignOutRow />

      <DangerZone />

      <Section title="Notifications" status={saving ? 'Saving…' : prefs ? 'Saved' : ''}>
        {prefs === null && !error && <p style={dim}>Loading…</p>}
        {error && <div style={errorBox}>{error}</div>}
        {prefs &&
          TOGGLES.map((t, i) => (
            <div key={t.key} style={{ borderTop: i === 0 ? 'none' : '1px solid var(--line)' }}>
              <label style={rowStyle}>
                <div style={{ flex: 1, paddingRight: 16 }}>
                  <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 2 }}>{t.title}</div>
                  <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.4 }}>{t.sub}</div>
                </div>
                <input
                  type="checkbox"
                  checked={prefs[t.key]}
                  onChange={toggle(t.key)}
                  style={{ width: 20, height: 20, accentColor: 'var(--coral)' }}
                />
              </label>
            </div>
          ))}
      </Section>
      </main>
    </>
  );
}

// ─── Danger zone: account deletion ─────────────────────────────────────
//
// 30-day soft-delete grace window. The handler revokes sessions
// immediately, so we clear local storage + bounce to landing on
// success. The reason picker is optional (helps retention analysis).
// The type-to-confirm input prevents misclick deletions — Apple/Google
// require deletion to be intentional, not single-tap.

const DELETE_REASONS = [
  'Not useful enough',
  'Privacy concern',
  'Found something better',
  'Taking a break',
  'Other',
];

/**
 * PlanBadge — branded display of the user's subscription state plus
 * a Manage CTA that opens the shared PaywallSheet (trigger='manage').
 *
 * Three visual tiers — each leans into the WYDM palette so the
 * Subscription section pulls its weight visually alongside Notifications
 * and Danger Zone, instead of reading as an empty white card:
 *
 *   Free:     deep ink card with coral accent. High-contrast brand
 *             statement, matches the FreeTierPaywall on /dm in spirit.
 *             "Manage" → opens the modal with the trigger='manage'
 *             copy pointing the user at the mobile app to subscribe.
 *   Premium:  coral-soft tint, italic-display plan name. "Manage"
 *             opens the same modal — copy explains that switching
 *             plan, updating billing, or cancelling all happen in
 *             the App Store / Play Store.
 *   Lifetime: full coral background, white text. No Manage button
 *             — there's literally nothing to manage (one-time
 *             purchase, no renewal, no plan switch).
 *
 * `user=null` shows a loading hint so the section doesn't jump as
 * /auth/me lands.
 */
// formatCancelDate renders the ISO datestring from the backend's
// subscription_expires_at field as a short, friendly local date
// like "Mar 15, 2026". Falls back to the raw string on parse failure.
function formatCancelDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function PlanBadge({ user }: { user: User | null }) {
  const [paywallOpen, setPaywallOpen] = useState(false);

  if (user === null) {
    return (
      <div style={{ padding: '20px 0', color: 'var(--ink-3)', fontSize: 13 }}>
        Loading…
      </div>
    );
  }

  const isPremium = user.tier === 'premium';
  const isLife    = user.plan === 'life';

  // Per-tier colour scheme. Free is the loud one — coral eyebrow on a
  // deep ink ground, so the Subscription section visually anchors the
  // page rather than disappearing into the bg. Premium + Lifetime
  // already had coral; tuned slightly for contrast with the new
  // ink-card free baseline.
  const tierColors = (() => {
    if (isLife) {
      return {
        bg:        'var(--coral)',
        fg:        '#fff',
        sub:       'rgba(255,255,255,0.82)',
        eyebrow:   'rgba(255,255,255,0.92)',
        btnBg:     'rgba(255,255,255,0.18)',
        btnFg:     '#fff',
        btnHover:  'rgba(255,255,255,0.28)',
      };
    }
    if (isPremium) {
      return {
        bg:        'var(--coral-soft)',
        fg:        'var(--ink)',
        sub:       'var(--ink-2)',
        eyebrow:   'var(--coral-ink)',
        btnBg:     'var(--ink)',
        btnFg:     'var(--bg)',
        btnHover:  'var(--ink-2)',
      };
    }
    // Free — the redesigned one.
    return {
      bg:        'var(--ink)',
      fg:        '#fff',
      sub:       'rgba(252,247,238,0.72)', // bg @ 72% over ink
      eyebrow:   'var(--coral)',
      btnBg:     'var(--coral)',
      btnFg:     '#fff',
      btnHover:  '#d75a3d', // coral, hand-darkened a hair on hover
    };
  })();

  // Display-name + subtitle copy. Free reads as a soft pitch; premium
  // confirms status + names the renewal cadence.
  //
  // Cancellation flips the renewal copy to a winding-down message
  // — "Active until then — no further charges." — so the badge no
  // longer reads as a healthy renewing sub once the user has
  // tapped Cancel in the App Store / Play Store sheet.
  //
  // `subscription_ended_at` covers the post-expiry case for up to
  // 30 days after period_end: the Free badge surfaces a
  // "Plan ended" pill so the user can confirm their cancellation
  // actually took effect rather than the badge silently looking
  // like a never-subscribed user.
  const cancelled = !!user.subscription_cancelled;
  const expiresAt = user.subscription_expires_at;
  const endedAt   = user.subscription_ended_at;
  const renewsAt  = user.subscription_renews_at;

  // Subtitle copy — the date is woven into the line itself
  // ("Expires on Mar 15, 2026.") rather than split into a
  // separate pill. Telling the user WHEN access ends is more
  // useful than telling them WHEN they pressed Cancel, and a
  // single sentence is less visually noisy than two overlapping
  // date references.
  let displayName: string;
  let subtitle:    string;
  if (!isPremium) {
    displayName = 'Free';
    subtitle = endedAt
      ? `Plan ended on ${formatCancelDate(endedAt)}.`
      : 'Unlock DMs + AI reviews by subscribing in the mobile app.';
  } else if (isLife) {
    displayName = 'Lifetime';
    subtitle    = 'Thanks for the early support — this one never expires.';
  } else if (user.plan === 'yearly') {
    displayName = 'Annual';
    if (cancelled && expiresAt) {
      subtitle = `Expires on ${formatCancelDate(expiresAt)}.`;
    } else if (renewsAt) {
      subtitle = `Renews on ${formatCancelDate(renewsAt)}.`;
    } else {
      subtitle = 'Renews yearly via your store account.';
    }
  } else {
    displayName = 'Monthly';
    if (cancelled && expiresAt) {
      subtitle = `Expires on ${formatCancelDate(expiresAt)}.`;
    } else if (renewsAt) {
      subtitle = `Renews on ${formatCancelDate(renewsAt)}.`;
    } else {
      subtitle = 'Renews monthly via your store account.';
    }
  }

  return (
    <>
      <div style={{
        padding: '22px 24px',
        borderRadius: 22,
        background: tierColors.bg,
        color: tierColors.fg,
        marginTop: 6,
      }}>
        <div
          className="mono"
          style={{
            fontSize: 10,
            color: tierColors.eyebrow,
            letterSpacing: 2,
            textTransform: 'uppercase',
            marginBottom: 8,
          }}
        >
          {isPremium ? 'WYDM Premium' : 'Current plan'}
        </div>
        <div
          className="serif"
          style={{
            fontSize: 34,
            fontWeight: 500,
            fontStyle: 'italic',
            lineHeight: 1.02,
            letterSpacing: -0.6,
          }}
        >
          {displayName}
        </div>

        <div
          style={{
            fontSize: 13.5,
            marginTop: 10,
            color: tierColors.sub,
            lineHeight: 1.5,
            maxWidth: 380,
          }}
        >
          {subtitle}
        </div>

        {/* Manage button — opens the standardised PaywallSheet modal
            with the 'manage' trigger. Hidden on lifetime since there's
            nothing recurring to change. Button colour adapts to the
            tier card's background so it stays high-contrast without
            visually competing. */}
        {!isLife && (
          <button
            type="button"
            onClick={() => setPaywallOpen(true)}
            style={{
              marginTop: 18,
              background: tierColors.btnBg,
              color: tierColors.btnFg,
              border: 'none',
              borderRadius: 100,
              padding: '10px 22px',
              fontSize: 13.5,
              fontWeight: 600,
              fontFamily: 'var(--font-ui)',
              cursor: 'pointer',
              transition: 'background 0.15s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = tierColors.btnHover; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = tierColors.btnBg; }}
          >
            Manage →
          </button>
        )}
      </div>

      <PaywallSheet
        open={paywallOpen}
        trigger="manage"
        onClose={() => setPaywallOpen(false)}
      />
    </>
  );
}

/**
 * Data export — GDPR Art. 20 portability. Synchronous download of a
 * JSON file with everything we hold about the caller. Rate-limited
 * server-side (3/day per user); 429 surfaces as the toast message.
 *
 * Sits as the first row inside Danger Zone — sensitive surface but
 * destructive only to your local Downloads folder.
 */
function DataExportRow() {
  const [downloading, setDownloading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [downloadedFilename, setDownloadedFilename] = useState<string | null>(null);

  const onDownload = async () => {
    setDownloading(true);
    setErr(null);
    setDownloadedFilename(null);
    try {
      const blob = await api.downloadDataExport();
      // Trigger save via a temporary anchor — standard browser pattern
      // for "save this blob as a file."
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const filename = `wydm-data-${new Date().toISOString().split('T')[0]}.json`;
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Defer revokeObjectURL — Safari needs the blob to outlive the
      // click event by a frame or two for the download to actually start.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setDownloadedFilename(filename);
    } catch (e) {
      if (e instanceof APIError && e.status === 429) {
        setErr("You can request 3 exports per day. Try again later.");
      } else {
        setErr(friendlyError(e, 'Settings'));
      }
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div>
      <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 6, color: 'var(--ink)' }}>
        Download your data
      </div>
      <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.5, marginBottom: 12 }}>
        Get a JSON file with everything we hold about you: profile,
        photos (as links), ratings you've given, AI reviews, messages,
        subscription history, and your audit log. Ratings of you by
        others are deliberately excluded — they're anonymous by design.
      </div>
      <button onClick={onDownload} disabled={downloading} style={{
        ...ctaBtn,
        opacity: downloading ? 0.5 : 1,
        cursor: downloading ? 'not-allowed' : 'pointer',
      }}>
        {downloading ? 'Preparing…' : 'Download data export'}
      </button>
      {err && (
        <div style={{ ...errorBox, marginTop: 12 }}>{err}</div>
      )}
      {downloadedFilename && !err && (
        <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 10 }}>
          Saved as <code style={{ fontFamily: 'ui-monospace, monospace' }}>{downloadedFilename}</code>.
        </div>
      )}
    </div>
  );
}

// SignOutRow — separate from DangerZone so the visual weight matches
// the routine action it is. Lives between Subscription and the
// red-bordered DangerZone. Sign-out is gated through a confirm dialog.
function SignOutRow() {
  const confirm = useSignOutConfirm();
  // The Btn primitive doesn't forward refs; wrap it in an inline-block
  // span so the popover has something to anchor its placement against.
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  return (
    <Section title="Session" status="">
      <div style={{ padding: '16px 0', display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 2 }}>Sign out</div>
          <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.4 }}>
            Sign out of this browser. Other devices stay signed in.
          </div>
        </div>
        <span ref={triggerRef} style={{ display: 'inline-block' }}>
          <Btn variant="outline" size="md" onClick={confirm.request}>Sign out</Btn>
        </span>
      </div>
      <SignOutConfirm open={confirm.open} onCancel={confirm.cancel} triggerRef={triggerRef} />
    </Section>
  );
}

function DangerZone() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<string>('');
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const canConfirm = confirmText.trim().toLowerCase() === 'delete my account';

  const onDelete = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    setErr(null);
    try {
      await api.deleteAccount({ reason: reason || undefined });
      // Backend already revoked all sessions. Clear local + bounce.
      sessionStore.clear();
      navigate('/', { replace: true, state: { deleted: true } });
    } catch (e) {
      setErr(friendlyError(e, 'Settings'));
      setSubmitting(false);
    }
  };

  return (
    <section style={{ marginTop: 48, marginBottom: 32 }}>
      <div className="mono" style={{ fontSize: 11, color: '#a8331f', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 10 }}>
        Danger zone
      </div>
      <div
        style={{
          background: 'var(--surface)',
          borderRadius: 22,
          padding: 24,
          boxShadow: 'var(--shadow-card)',
          border: '1px solid rgba(208,64,48,0.15)',
        }}
      >
        <DataExportRow />

        <div style={{ borderTop: '1px solid var(--line)', margin: '20px 0' }} />

        <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 6, color: 'var(--ink)' }}>
          Delete your account
        </div>
        <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.5, marginBottom: 16 }}>
          Your profile is hidden immediately. After 30 days we permanently
          delete your photos, ratings, messages, and account record. Active
          web subscriptions are cancelled at the end of the current paid
          period.
        </div>
        {!open ? (
          <button
            onClick={() => setOpen(true)}
            style={dangerBtn}
          >
            Delete account…
          </button>
        ) : (
          <div style={{ marginTop: 8 }}>
            <label style={{ display: 'block', fontSize: 12, color: 'var(--ink-3)', marginBottom: 6 }}>
              Reason (optional)
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px',
                fontFamily: 'inherit', fontSize: 14,
                background: 'var(--bg)', color: 'var(--ink)',
                border: '1px solid var(--line)', borderRadius: 8,
                marginBottom: 14,
              }}
            >
              <option value="">Don't say</option>
              {DELETE_REASONS.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>

            <label style={{ display: 'block', fontSize: 12, color: 'var(--ink-3)', marginBottom: 6 }}>
              Type <code style={{ background: 'rgba(208,64,48,0.08)', padding: '0 6px', borderRadius: 3 }}>delete my account</code> to confirm
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              autoComplete="off"
              placeholder="delete my account"
              style={{
                width: '100%', padding: '10px 12px',
                fontFamily: 'inherit', fontSize: 14,
                background: 'var(--bg)', color: 'var(--ink)',
                border: '1px solid var(--line)', borderRadius: 8,
                marginBottom: 14,
              }}
            />

            {err && <div style={errorBox}>{err}</div>}

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => { setOpen(false); setConfirmText(''); setReason(''); setErr(null); }} style={ghostBtn}>
                Cancel
              </button>
              <button
                onClick={onDelete}
                disabled={!canConfirm || submitting}
                style={{
                  ...dangerBtn,
                  opacity: (!canConfirm || submitting) ? 0.5 : 1,
                  cursor: (!canConfirm || submitting) ? 'not-allowed' : 'pointer',
                }}
              >
                {submitting ? 'Deleting…' : 'Yes, delete my account'}
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

const dangerBtn: React.CSSProperties = {
  background: '#a8331f',
  color: '#fff',
  border: 'none',
  borderRadius: 100,
  padding: '10px 22px',
  fontWeight: 600,
  fontSize: 14,
  cursor: 'pointer',
};

const ghostBtn: React.CSSProperties = {
  background: 'transparent',
  color: 'var(--ink)',
  border: '1px solid var(--line-strong)',
  borderRadius: 100,
  padding: '10px 22px',
  fontWeight: 600,
  fontSize: 14,
  cursor: 'pointer',
};

function Section({
  title,
  status,
  children,
}: {
  title: string;
  status: string;
  children: React.ReactNode;
}) {
  return (
    <section style={{ marginBottom: 32 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
        <div className="mono" style={{ fontSize: 11, color: 'var(--coral)', letterSpacing: 2, textTransform: 'uppercase' }}>
          {title}
        </div>
        <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: 1, textTransform: 'uppercase' }}>
          {status}
        </div>
      </div>
      {/* Bare wrapper — surface/shadow card chrome removed; rows sit
          on the page bg + use their own borderTop dividers for rhythm.
          DangerZone keeps its own bordered card for the destructive
          semantics. */}
      <div>{children}</div>
    </section>
  );
}

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: '16px 0',
  cursor: 'pointer',
  userSelect: 'none',
};

const ctaBox: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  background: 'var(--surface)',
  borderRadius: 18,
  padding: '20px 24px',
  marginBottom: 24,
  boxShadow: 'var(--shadow-card)',
};

const ctaBtn: React.CSSProperties = {
  background: 'var(--coral)',
  color: '#fff',
  border: 'none',
  borderRadius: 100,
  padding: '10px 22px',
  fontWeight: 600,
  fontSize: 14,
  cursor: 'pointer',
  flexShrink: 0,
};

const mutedNotice: React.CSSProperties = {
  background: 'rgba(43, 29, 20, 0.04)',
  color: 'var(--ink-2)',
  border: '1px solid var(--line)',
  borderRadius: 12,
  padding: '12px 16px',
  fontSize: 13,
  marginBottom: 24,
};

const errorBox: React.CSSProperties = {
  background: 'rgba(208,64,48,0.08)',
  color: '#a8331f',
  border: '1px solid rgba(208,64,48,0.2)',
  borderRadius: 12,
  padding: '10px 14px',
  fontSize: 13,
};

const dim: React.CSSProperties = { color: 'var(--ink-3)', padding: 16 };
